import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Button,
  InlineLoading,
  Loading,
  InlineNotification
} from 'carbon-components-react';
import { Checkmark16 } from '@carbon/icons-react';
import { currentUser } from 'redux/session/selectors';
import { selectTeamsList, selectLoadingTeamsList } from 'redux/team/selectors';
import { useComponentMount, useEmptyCallback } from 'utilities/hooks.util';
import { TeamQueryParams, TEAM_NAME, SCREEN_SIZE } from 'types';
import { getTeamsListStart } from 'redux/team/actions';

import { useSaga } from 'use-reducer-saga';
import { moveEnquiryTeamStart } from './localStore/actions';
import reducer, { initialState } from './localStore/reducer';
import sagas from './localStore/sagas';

const MoveEnquiryToTeam: React.FC<Props> = ({
  enquiryId,
  enquiryUserId,
  enquiryTeamId,
  refreshMap,
  setNeedRefresh,
  onRefreshTeamView,
  onGoToEnquiry,
  jobId,
  isAutoRenew,
  enquiryAutoRenewSeries,
  screenSize
}) => {
  const user = useSelector(currentUser);
  const teams = useSelector(selectTeamsList);
  const loadingTeams = useSelector(selectLoadingTeamsList);
  const dispatch: any = useDispatch();
  const [localStore, localDispatch] = useSaga(reducer, initialState, sagas);
  const { moveEnquiryTeam, isMovingEnquiryTeam } = localStore;
  const isARPartOfSeries: boolean = isAutoRenew
    ? enquiryAutoRenewSeries.length > 1
    : false;
  const [localMoving, setLocalMoving] = useState<boolean>(false);
  const [hidePrivate, setHidePrivate] = useState<boolean>(false);
  const [selected, setSelected] = useState<number>(enquiryTeamId || 0);
  const [selectedTeamName, setSelectedTeamName] = useState<string>('');
  const [disableMoveButton, setDisableMoveButton] = useState<boolean>(true);
  const [moveSuccessful, setMoveSuccessful] = useState<boolean>(false);
  const [enquiriesToMove, setEnquiriesToMove] = useState<Array<any>>([]);

  const onSelectTeam = (teamId: number, teamName: string) => {
    setSelected(teamId);
    setSelectedTeamName(teamName);
    if (enquiryTeamId === null && teamId === 0) {
      setDisableMoveButton(true);
    } else if (enquiryTeamId !== teamId) {
      setDisableMoveButton(false);
    } else {
      setDisableMoveButton(true);
    }
  };

  const handleMoveClick = (moveAll: boolean = false) => {
    setLocalMoving(true);
    const data: Array<any> = [];
    const enquiryIdArray: Array<any> = [];

    if (isARPartOfSeries && enquiryAutoRenewSeries.length > 1 && moveAll) {
      enquiryAutoRenewSeries.forEach((enq) => {
        const params = {
          enquiryId: enq.id,
          jobId: enq.jobId,
          currentTeamId: enquiryTeamId,
          newTeamId: selected
        };
        data.push(params);
        enquiryIdArray.push(enq.id);
      });
    } else {
      const params = {
        enquiryId,
        jobId,
        currentTeamId: enquiryTeamId,
        newTeamId: selected
      };
      data.push(params);
    }

    const params = {
      enquiryIds: enquiryIdArray,
      enquiryId,
      jobId,
      currentTeamId: enquiryTeamId,
      newTeamId: selected
    };

    setEnquiriesToMove(data);
    localDispatch(moveEnquiryTeamStart(params));
  };

  const renderMapAgain = useEmptyCallback(
    () => typeof refreshMap === 'function' && refreshMap()
  );

  useEffect(() => {
    if (moveEnquiryTeam && !isMovingEnquiryTeam) {
      setNeedRefresh(true);
      renderMapAgain();
      setTimeout(() => {
        setLocalMoving(false);
        setDisableMoveButton(true);
        setMoveSuccessful(true);
      }, 1500);
    }
  }, [moveEnquiryTeam, isMovingEnquiryTeam, setNeedRefresh, renderMapAgain]);

  useEffect(() => {
    // hide private team option when
    // 1) is external user that not turn from trial
    // 2) is external user which turn from trial in create enquiry page.
    // 3) If the enquiry is not own by the user and the enquiry already in a team
    if (user) {
      if (user.is_as_external_user && !user.is_trial) {
        setHidePrivate(!user.trial_organisation_name?.trim());
      } else if (enquiryTeamId && user.id !== enquiryUserId) {
        setHidePrivate(true);
      } else {
        setHidePrivate(false);
      }
    }
  }, [user, enquiryUserId, enquiryTeamId]);

  useComponentMount(() => {
    const params: TeamQueryParams = {
      sorting: 'name,asc'
    };
    !teams && dispatch(getTeamsListStart(params));
  });

  return (
    <div className={`move-enquiry-to-team-content`}>
      {isARPartOfSeries && !moveSuccessful && (
        <div className="inline-notification-container">
          <InlineNotification
            kind="info"
            lowContrast
            hideCloseButton={true}
            title={'This enquiry is part of a series'}
            className="inline-notification"
          />
        </div>
      )}
      <div
        className={`move-enquiry-to-team-content-wrapper ${
          moveSuccessful ? 'moved' : ''
        }`}
      >
        {!moveSuccessful ? (
          <div className="move-enquiry-to-team-content-selection">
            <div className={`move-enquiry-to-team-content-teams-list`}>
              {!loadingTeams && (
                <>
                  {!hidePrivate && (
                    <div className="list-container">
                      <div className="checkmark">
                        {selected === 0 && <Checkmark16 />}
                      </div>
                      <div
                        className={`list ${selected === 0 ? 'selected' : ''} `}
                        aria-label="Private team"
                        onClick={() => onSelectTeam(0, 'Private team')}
                      >
                        {TEAM_NAME.PRIVATE}
                      </div>
                    </div>
                  )}
                  {teams &&
                    teams.map((team: any, inx: number) => (
                      <div className="list-container" key={`team-${inx}`}>
                        <div className="checkmark">
                          {selected === team.id && <Checkmark16 />}
                        </div>
                        <div
                          className={`list ${
                            selected === team.id ? 'selected' : ''
                          } `}
                          aria-label={team.name}
                          onClick={() => onSelectTeam(team.id, team.name)}
                        >
                          {team.name}
                        </div>
                      </div>
                    ))}
                </>
              )}
              {loadingTeams && (
                <div className="loading-team-list-container">
                  <Loading withOverlay={false} />
                </div>
              )}
            </div>
            <div
              className={`move-enquiry-to-team-content-button-container ${
                screenSize === SCREEN_SIZE.SM && isARPartOfSeries
                  ? 'side-margins'
                  : ''
              }`}
            >
              {isARPartOfSeries && (
                <Button
                  kind={screenSize === SCREEN_SIZE.SM ? 'tertiary' : 'ghost'}
                  disabled={
                    disableMoveButton || isMovingEnquiryTeam || localMoving
                  }
                  onClick={() => handleMoveClick()}
                  className={`${
                    isARPartOfSeries
                      ? 'multiple-action-button'
                      : 'action-button'
                  }`}
                >
                  {!isMovingEnquiryTeam && !localMoving ? (
                    'Move only this enquiry'
                  ) : !isMovingEnquiryTeam &&
                    localMoving &&
                    enquiriesToMove.length <= 1 ? (
                    <InlineLoading
                      description={`Move only this enquiry`}
                      status={'finished'}
                      aria-live={'assertive'}
                    />
                  ) : isMovingEnquiryTeam &&
                    localMoving &&
                    enquiriesToMove.length <= 1 ? (
                    <InlineLoading
                      description={`Move only this enquiry`}
                      status={'active'}
                      aria-live={'assertive'}
                    />
                  ) : (
                    'Move only this enquiry'
                  )}
                </Button>
              )}

              <Button
                kind="secondary"
                disabled={
                  disableMoveButton || isMovingEnquiryTeam || localMoving
                }
                onClick={() => handleMoveClick(isARPartOfSeries ? true : false)}
                className={`${
                  isARPartOfSeries ? 'multiple-action-button' : 'action-button'
                }`}
              >
                {!isMovingEnquiryTeam && !localMoving ? (
                  isARPartOfSeries ? (
                    `Move series (${enquiryAutoRenewSeries.length})`
                  ) : (
                    'Move'
                  )
                ) : isARPartOfSeries && enquiriesToMove.length > 1 ? (
                  <InlineLoading
                    description={
                      !isMovingEnquiryTeam
                        ? `Move series (${enquiryAutoRenewSeries.length})`
                        : isARPartOfSeries
                        ? `Move series (${enquiryAutoRenewSeries.length})`
                        : 'Move'
                    }
                    status={!isMovingEnquiryTeam ? 'finished' : 'active'}
                    aria-live={'assertive'}
                  />
                ) : !isARPartOfSeries ? (
                  <InlineLoading
                    description={
                      !isMovingEnquiryTeam
                        ? 'Moved'
                        : isARPartOfSeries
                        ? `Move series (${enquiryAutoRenewSeries.length})`
                        : 'Move'
                    }
                    status={!isMovingEnquiryTeam ? 'finished' : 'active'}
                    aria-live={'assertive'}
                  />
                ) : isARPartOfSeries ? (
                  `Move series (${enquiryAutoRenewSeries.length})`
                ) : (
                  'Move'
                )}
              </Button>
            </div>
          </div>
        ) : (
          <div className="move-enquiry-to-team-content-success">
            <div className="move-enquiry-to-team-content-success-body">
              <div className="move-enquiry-to-team-content-success-body-title">
                {enquiriesToMove.length > 1
                  ? `${enquiryAutoRenewSeries.length} enquiries moved`
                  : 'Enquiry moved'}
              </div>
              <div className="move-enquiry-to-team-content-success-body-teams-list">
                <div className="list-container">
                  <div className="checkmark">
                    <Checkmark16 />
                  </div>
                  <div>{selectedTeamName}</div>
                </div>
              </div>
            </div>
            <div className="move-enquiry-to-team-content-success-footer">
              <Button
                kind={screenSize === SCREEN_SIZE.SM ? 'tertiary' : 'ghost'}
                onClick={() => onRefreshTeamView()}
              >
                Refresh current team view
              </Button>
              <Button
                kind="secondary"
                onClick={() =>
                  onGoToEnquiry(
                    selected,
                    enquiriesToMove.slice(-1)[0].enquiryId,
                    enquiriesToMove.slice(-1)[0].jobId
                  )
                }
              >
                {enquiriesToMove.length > 1
                  ? 'Go to latest enquiry'
                  : 'Go to enquiry'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

type Props = {
  refreshMap?: Function;
  enquiryTeamId: number;
  enquiryId: number;
  enquiryUserId: number;
  closeDetailsPanel: Function;
  setNeedRefresh: Function;
  onRefreshTeamView: Function;
  onGoToEnquiry: Function;
  jobId: number | string;
  isAutoRenew: boolean;
  enquiryAutoRenewSeries: any[];
  screenSize: any;
};

export default React.memo(MoveEnquiryToTeam);
